import React from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

/**
 * InvoicePreview - Live preview component for invoices
 */
export const InvoicePreview = ({ 
  invoice, 
  company = {}, 
  type = "invoice",
  scale = 0.6 
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const calculateLineTotal = (line) => {
    const quantity = parseFloat(line.quantity) || 0;
    const unitPrice = parseFloat(line.unit_price) || 0;
    const discount = parseFloat(line.discount_percent) || 0;
    const subtotal = quantity * unitPrice;
    return subtotal - (subtotal * discount / 100);
  };

  const calculateTotals = () => {
    const lines = invoice.lines || [];
    let subtotal = 0;
    let vatAmount = 0;
    
    lines.forEach(line => {
      const lineTotal = calculateLineTotal(line);
      subtotal += lineTotal;
      vatAmount += lineTotal * ((parseFloat(line.vat_rate) || 25) / 100);
    });
    
    return {
      subtotal,
      vatAmount,
      total: subtotal + vatAmount
    };
  };

  const totals = calculateTotals();
  const isQuote = type === "quote";
  const title = isQuote ? "OFFERT" : "FAKTURA";
  const numberLabel = isQuote ? "Offertnr" : "Fakturanr";
  const dateLabel = isQuote ? "Offertdatum" : "Fakturadatum";
  const dueDateLabel = isQuote ? "Giltig t.o.m" : "Förfallodatum";

  return (
    <div 
      className="bg-white shadow-lg rounded-lg overflow-hidden border border-slate-200"
      style={{ 
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        width: `${100/scale}%`,
        minHeight: "400px"
      }}
    >
      {/* A4-like preview */}
      <div className="p-6 space-y-4" style={{ minHeight: "560px" }}>
        {/* Header */}
        <div className="flex justify-between items-start border-b border-slate-200 pb-4">
          <div>
            <h2 className="text-lg font-bold text-indigo-600">
              {company.name || "Ditt Företag AB"}
            </h2>
            <p className="text-xs text-slate-500">
              {company.address || "Adress"}<br/>
              {company.postal_code || "123 45"} {company.city || "Stad"}<br/>
              {company.org_number && `Org.nr: ${company.org_number}`}
            </p>
          </div>
          <div className="text-right">
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
            <div className="text-xs text-slate-600 mt-1 space-y-0.5">
              <p>{numberLabel}: <span className="font-medium">{invoice.invoice_number || invoice.quote_number || "---"}</span></p>
              <p>{dateLabel}: <span className="font-medium">{invoice.invoice_date || invoice.quote_date || format(new Date(), "yyyy-MM-dd")}</span></p>
              <p>{dueDateLabel}: <span className="font-medium">{invoice.due_date || invoice.valid_until || "---"}</span></p>
            </div>
          </div>
        </div>

        {/* Customer */}
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs font-semibold text-slate-600 mb-1">{isQuote ? "Till:" : "Kund:"}</p>
          <p className="text-sm font-medium text-slate-800">
            {invoice.customer_name || "Välj kund..."}
          </p>
          {invoice.our_reference && (
            <p className="text-xs text-slate-500 mt-1">Vår ref: {invoice.our_reference}</p>
          )}
          {invoice.your_reference && (
            <p className="text-xs text-slate-500">Er ref: {invoice.your_reference}</p>
          )}
        </div>

        {/* Line items table */}
        <div className="mt-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-indigo-600 text-white">
                <th className="text-left py-1.5 px-2 rounded-tl">Beskrivning</th>
                <th className="text-right py-1.5 px-2 w-16">Antal</th>
                <th className="text-right py-1.5 px-2 w-20">à-pris</th>
                {!isQuote && <th className="text-right py-1.5 px-2 w-14">Moms</th>}
                <th className="text-right py-1.5 px-2 w-24 rounded-tr">Summa</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.lines || []).length === 0 ? (
                <tr>
                  <td colSpan={isQuote ? 4 : 5} className="text-center py-6 text-slate-400 italic">
                    Lägg till rader...
                  </td>
                </tr>
              ) : (
                (invoice.lines || []).map((line, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "bg-slate-50" : ""}>
                    <td className="py-1.5 px-2 text-slate-700">
                      {line.description || "-"}
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-600">
                      {line.quantity || 0} {line.unit || "st"}
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-600">
                      {formatCurrency(line.unit_price)}
                    </td>
                    {!isQuote && (
                      <td className="py-1.5 px-2 text-right text-slate-600">
                        {line.vat_rate || 25}%
                      </td>
                    )}
                    <td className="py-1.5 px-2 text-right font-medium text-slate-700">
                      {formatCurrency(calculateLineTotal(line))} kr
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mt-4 flex justify-end">
          <div className="w-48 space-y-1 text-xs">
            <div className="flex justify-between text-slate-600">
              <span>Netto:</span>
              <span>{formatCurrency(totals.subtotal)} kr</span>
            </div>
            {!isQuote && (
              <div className="flex justify-between text-slate-600">
                <span>Moms:</span>
                <span>{formatCurrency(totals.vatAmount)} kr</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-sm text-indigo-600 border-t border-slate-200 pt-1">
              <span>{isQuote ? "Totalt:" : "Att betala:"}</span>
              <span>{formatCurrency(isQuote ? totals.subtotal : totals.total)} kr</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="mt-4 text-xs text-slate-500 border-t border-slate-100 pt-2">
            <span className="font-medium">Meddelande: </span>
            {invoice.notes}
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto pt-4 border-t border-slate-100 text-xs text-slate-400 text-center">
          Genererad av yubi.ai
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
